import { useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import InstallmentModal from "../components/InstallmentModal";
import OtherFeeModal from "../components/OtherFeeModal";

const Step2Financial = ({ form, setForm, onNext, onBack }) => {
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [showOtherFeeModal, setShowOtherFeeModal] = useState(false);

  const [error, setError] = useState("");

  const addInstallment = (data) => {
    setForm({
      ...form,
      installments: [...(form.installments || []), data],
    });
  };

  const addOtherFee = (data) => {
    setForm({
      ...form,
      other_fees: [...(form.other_fees || []), data],
    });
  };

  const removeInstallment = (index) => {
    const updated = [...form.installments];
    updated.splice(index, 1);
    setForm({ ...form, installments: updated });
  };

  const removeOtherFee = (index) => {
    const updated = [...form.other_fees];
    updated.splice(index, 1);
    setForm({ ...form, other_fees: updated });
  };

  const validate = () => {
    if (!form.currency || !form.artist_fee || !form.booking_fee) {
      setError("* Marked fields are required.");
      return false;
    }

    setError("");
    return true;
  };

  const handleNext = () => {
    if (!validate()) return;
    onNext();
  };

  return (
    <div className="space-y-8">
      <h2 className="text-lg font-semibold text-white">
        Financial Information
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* LEFT SIDE */}
        <div className="space-y-6">
          {/* Currency */}
          <div>
            <label className="text-sm text-slate-300">Select Currency*</label>

            <select
              value={form.currency || ""}
              onChange={(e) => {
                setForm({ ...form, currency: e.target.value });
                setError("");
              }}
              className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-white"
            >
              <option value="">Select</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
            </select>
          </div>

          {/* Artist Fee */}
          <div>
            <label className="text-sm text-slate-300">Artist Fee*</label>

            <input
              type="number"
              value={form.artist_fee || ""}
              onChange={(e) => {
                setForm({ ...form, artist_fee: e.target.value });
                setError("");
              }}
              className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-white"
            />
          </div>

          {/* Booking Fee */}
          <div>
            <label className="text-sm text-slate-300">Booking Fee*</label>

            <input
              type="number"
              value={form.booking_fee || ""}
              onChange={(e) => {
                setForm({ ...form, booking_fee: e.target.value });
                setError("");
              }}
              className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2 text-white"
            />
          </div>

          <div>
            {/* Other Fees */}
            <div className="flex items-center mb-2">
              <span className="text-sm text-slate-300">Other Fees</span>

              <button
                onClick={() => setShowOtherFeeModal(true)}
                className="text-pink-500 hover:text-pink-400 ml-3"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Other Fees Table */}
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm text-slate-300">
                <thead className="bg-slate-800 text-xs">
                  <tr>
                    <th className="p-2 text-left">Fee Description</th>
                    <th className="p-2 text-left">Amount</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {form.other_fees.map((fee, i) => (
                    <tr key={i} className="border-t border-white/10">
                      <td className="p-2">{fee.description}</td>
                      <td className="p-2">{fee.amount}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => removeOtherFee(i)}
                          className="text-red-400"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div>
          <div className="flex items-center mb-2">
            <h3 className="text-sm text-slate-300">Payment Installments</h3>

            <button
              onClick={() => setShowInstallmentModal(true)}
              className="text-pink-500 hover:text-pink-400 ml-3"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Installment Table */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm text-slate-300">
              <thead className="bg-slate-800 text-xs">
                <tr>
                  <th className="p-2 text-left">Due Date</th>
                  <th className="p-2 text-left">Amount</th>
                  <th className="p-2 text-left">VAT</th>
                  <th className="p-2 text-left">Total Invoice</th>
                  <th className="p-2 text-left">Document</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {form.installments?.map((inst, i) => (
                  <tr key={i} className="border-t border-white/10">
                    <td className="p-2">{inst.due_date}</td>
                    <td className="p-2">{inst.amount}</td>
                    <td className="p-2">{inst.vat}</td>
                    <td className="p-2">{inst.total}</td>
                    <td className="p-2">{inst.document_type}</td>

                    <td className="p-2 text-right">
                      <button
                        onClick={() => removeInstallment(i)}
                        className="text-red-400"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center gap-3 pt-6">
        {error && <p className="text-red-400 text-sm">{error}</p>}
      </div>

      {/* NEXT BUTTON */}
      <div className="flex justify-center">
        <button
          onClick={onBack}
          className="bg-slate-400 hover:bg-pink-700 px-10 py-3 rounded-lg text-white font-medium mr-5"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="bg-pink-600 hover:bg-pink-700 px-10 py-3 rounded-lg text-white font-medium"
        >
          Next
        </button>
      </div>

      {/* MODALS */}
      {showInstallmentModal && (
        <InstallmentModal
          onClose={() => setShowInstallmentModal(false)}
          onSave={addInstallment}
        />
      )}

      {showOtherFeeModal && (
        <OtherFeeModal
          onClose={() => setShowOtherFeeModal(false)}
          onSave={addOtherFee}
        />
      )}
    </div>
  );
};

export default Step2Financial;
